import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useDeleteCampaign } from "@/lib/queries";
import type { Campaign } from "@/lib/types";

/**
 * Delete a campaign — an admin's.
 *
 * Core-api refuses while something still needs the campaign: a number routed to it, a rep signed
 * in to one of its lines, a trigger that sends it leads, an outcome that hands leads to it, or the
 * calling campaign (ACL-498). Deleting used to take the trigger with it, and the next lead from
 * GHL or a zap was lost. The refusal names each one, so it stays here in the dialog until the
 * admin closes it: a toast is gone before a list of names is read.
 */
export function DeleteCampaignDialog({
  open, onOpenChange, campaign, onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign | undefined;
  /** After the delete: close the dialog, or leave the campaign's page, which is now a 404. */
  onDeleted: () => void;
}) {
  const toast = useToast();
  const remove = useDeleteCampaign();
  const [problem, setProblem] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (open) setProblem(null);
  }, [open]);
  const submit = () => {
    if (!campaign) return;
    remove.mutate(campaign.id, {
      onSuccess: () => {
        toast.success("Campaign deleted", campaign.name);
        onDeleted();
      },
      onError: (e) => setProblem(e instanceof ApiError ? e.message : "Could not delete. Please try again."),
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Delete this campaign?</DialogTitle>
          <DialogDescription>
            <span className="font-medium">{campaign?.name}</span> and its lines will be removed. Numbers
            routed to it, reps signed in to its lines, and the triggers and outcomes that send it leads
            block this.
          </DialogDescription>
        </DialogHeader>
        {problem && (
          <DialogBody>
            <Callout tone="danger">{problem}</Callout>
          </DialogBody>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="danger" loading={remove.isPending} disabled={problem !== null} onClick={submit}>
            Delete campaign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
